import type { BookingStatus } from '@massilia/prisma-generated'

import {
  type MyBookingDetail,
  useDownloadMyBookingInvoicePdfMutation,
  useDownloadMyBookingReservationContractPdfMutation,
  useDownloadMyTripETicketPdfMutation,
} from '@massilia/queries'
import { Button, downloadBlob } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { Download, FileText, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { SummaryRow } from '@/components/payment-confirmation/summary-row'
import { centsToEuros, formatPrice } from '@/utils/format-price'

import { bookingPnrLabel } from './booking-pnr-label'

interface BookingSummaryCardProps {
  booking: MyBookingDetail
}

export function BookingSummaryCard({ booking }: BookingSummaryCardProps) {
  const title = getStatusTitle(booking.status)
  const downloadETicketMutation = useDownloadMyTripETicketPdfMutation()
  const downloadInvoiceMutation = useDownloadMyBookingInvoicePdfMutation()
  const downloadContractMutation = useDownloadMyBookingReservationContractPdfMutation()
  // Un billet par voyage : le couple dont l'un rentre seul a deux billets distincts.
  const activeTrips = booking.trips.filter((trip) => !trip.cancelledAt)
  const isMultiTrip = activeTrips.length > 1
  // Le nom des fichiers facture et contrat reste sur le premier PNR : c'est une
  // référence technique, pas un affichage.
  const documentRef = booking.pnr ?? booking.reference

  const handleDownloadETicket = async (trip: MyBookingDetail['trips'][number]) => {
    try {
      const blob = await downloadETicketMutation.mutateAsync({
        bookingId: booking.id,
        tripId: trip.id,
      })
      downloadBlob(blob, `billet-${trip.pnr}.pdf`)
    } catch {
      toast.error('Erreur lors du téléchargement du billet')
    }
  }

  const handleDownloadInvoice = async () => {
    try {
      const blob = await downloadInvoiceMutation.mutateAsync(booking.id)
      downloadBlob(blob, `facture-${documentRef}.pdf`)
    } catch {
      toast.error('Erreur lors du téléchargement de la facture')
    }
  }

  const handleDownloadContract = async () => {
    try {
      const blob = await downloadContractMutation.mutateAsync(booking.id)
      downloadBlob(blob, `contrat-${documentRef}.pdf`)
    } catch {
      toast.error('Erreur lors du téléchargement du contrat')
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-light-200 bg-white p-4 md:p-6">
      <div className="text-lg font-semibold text-gray-light-900 sm:text-2xl">{title}</div>

      <SummaryRow label="Numéro de dossier" value={bookingPnrLabel(booking)} />
      {booking.airlineReferences.length > 0 && (
        <SummaryRow
          label="Référence compagnie"
          value={booking.airlineReferences
            .map((ref) => (ref.airlineName ? `${ref.code} (${ref.airlineName})` : ref.code))
            .join(', ')}
        />
      )}
      <SummaryRow label="Adresse email" value={booking.client.email ?? undefined} />
      <SummaryRow label="Téléphone" value={booking.client.phoneNumbers?.[0]?.number ?? undefined} />
      <SummaryRow label="Tarif" value={formatPrice(centsToEuros(booking.totalPrice))} />

      <div className="mt-2 flex flex-wrap gap-3">
        {/* Séjour sans PNR : le voucher et les documents du prestataire tiennent lieu de billet. */}
        {booking.documents.map((file) => (
          <Button asChild className="max-w-full text-xs sm:text-sm" key={file.id}>
            <a href={file.url}>
              <Download className="size-4" />
              <span className="truncate">Télécharger {file.fileName}</span>
            </a>
          </Button>
        ))}
        {booking.documents.length === 0 &&
          activeTrips.map((trip) => {
            const isTicketIssued = trip.passengers.some((p) => p.ticketNumbers.length > 0)
            const isPending =
              downloadETicketMutation.isPending &&
              downloadETicketMutation.variables.tripId === trip.id
            return (
              <Button
                className="text-xs sm:text-sm"
                disabled={!isTicketIssued}
                key={trip.id}
                onClick={() => {
                  void handleDownloadETicket(trip)
                }}
                title={isTicketIssued ? undefined : "Le billet n'est pas encore émis"}
                type="button"
              >
                {isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Download className="size-4" />
                )}
                <span>
                  {isMultiTrip
                    ? `Billet ${trip.pnr} (${trip.departureLocation} → ${trip.arrivalLocation})`
                    : 'Télécharger le billet'}
                </span>
              </Button>
            )
          })}
        <Button
          className="text-xs sm:text-sm"
          onClick={() => {
            void handleDownloadInvoice()
          }}
          type="button"
        >
          {downloadInvoiceMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <FileText className="size-4" />
          )}
          <span>Télécharger la facture</span>
        </Button>
        <Button
          className="text-xs sm:text-sm"
          onClick={() => {
            void handleDownloadContract()
          }}
          type="button"
        >
          {downloadContractMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <FileText className="size-4" />
          )}
          <span>Télécharger le contrat</span>
        </Button>
        <Link to="." href="https://wp.massiliavoyages.fr/conditions-generales-vente-vols-secs/">
          <Button className="text-xs sm:text-sm" type="button" variant="outline">
            <FileText className="size-4" />
            Conditions générales de ventes
          </Button>
        </Link>
      </div>
    </div>
  )
}

function getStatusTitle(status: BookingStatus): string {
  switch (status) {
    case 'AWAITING_DEPOSIT': {
      return 'Votre réservation est en attente de règlement'
    }
    case 'CANCELLED': {
      return 'Ce dossier a été annulé'
    }
    case 'DEPOSIT_PAID': {
      return 'Votre réservation est confirmée !'
    }
    case 'FULLY_PAID_PENDING':
    case 'FULLY_PAID_TRAVELED': {
      return 'Votre réservation est finalisée !'
    }
    default: {
      return 'Votre réservation'
    }
  }
}
